import { Component, OnDestroy, effect, HostListener, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../auth/components/auth.service';
import { User } from '../../../auth/shared/UserInterface';
import { toObservable } from '@angular/core/rxjs-interop';
import { AppRoutes } from '../../constants/routes';
import { OrganizationService } from '../../services/organization.service';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ADMIN_NAV_ITEMS } from '../../constants/admin-navigation';
import { IconType } from '../../../shared/services/icon.service';

interface NavLink {
  path: AppRoutes;
  title: string;
  exact?: boolean;
  queryParams?: { [key: string]: string };
  isAdminMenu?: boolean;
  icon?: IconType;
}

interface AccountLink {
  path: AppRoutes;
  title: string;
  icon: IconType;
}

@Component({
  selector: 'app-nav',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, IconComponent],
  templateUrl: './nav.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./nav.component.css']
})
export class NavComponent implements OnDestroy {
  currentUser$: Observable<User | null | undefined> = of(null);

  isMenuOpen = false;
  isAdmin = false;
  isAdminExpanded = false;
  isOrgDropdownOpen = false;
  private destroy$ = new Subject<void>();

  readonly IconType = IconType;
  readonly adminNavItems = ADMIN_NAV_ITEMS;

  navigationLinks: NavLink[] = [];

  accountLinks: AccountLink[] = [
    { path: AppRoutes.PROFILE, title: 'Dein Profil', icon: IconType.USER },
    { path: AppRoutes.BOOKINGS, title: 'Deine Buchungen', icon: IconType.CALENDAR },
    { path: AppRoutes.MY_SUBSCRIPTIONS, title: 'Deine Abos', icon: IconType.REFRESH },
    { path: AppRoutes.VOUCHERS, title: 'Deine Gutscheine', icon: IconType.VOUCHER },
    { path: AppRoutes.MY_INVOICES, title: 'Deine Rechnungen', icon: IconType.EURO },
  ];

  authLinks: NavLink[] = [
    { path: AppRoutes.LOGIN, title: 'Einloggen' },
    { path: AppRoutes.LOGIN, title: 'Registrieren', queryParams: { mode: 'signup' } }
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    public organizationService: OrganizationService,
    private elementRef: ElementRef
  ) {
    this.currentUser$ = toObservable(this.authService.currentUserSignal);

    effect(() => {
      this.authService.currentUserSignal();
      this.organizationService.selectedOrganizationId();
      this.updateAdminStatus();
      this.updateNavigationLinks();
    });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isMenuOpen = false;
        this.isAdminExpanded = false;
        this.isOrgDropdownOpen = false;
      });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target;
    if (!(target instanceof Node) || !this.elementRef.nativeElement.contains(target)) {
      this.isMenuOpen = false;
      this.isOrgDropdownOpen = false;
      this.isAdminExpanded = false;
    }
  }

  get selectedOrganization() {
    return this.organizationService.getSelectedOrganization();
  }

  private updateAdminStatus(): void {
    const selectedOrganizationId = this.organizationService.selectedOrganizationId();
    this.isAdmin = selectedOrganizationId
      ? this.authService.isAdminForOrganization(selectedOrganizationId)
      : false;
  }

  private updateNavigationLinks(): void {
    const baseLinks: NavLink[] = [
      { path: AppRoutes.BOOK, title: 'Buchen', icon: IconType.CALENDAR }
    ];

    if (this.authService.currentUserSignal()) {
      baseLinks.push({ path: AppRoutes.COURSE_LIST, title: 'Kurse', icon: IconType.BOOK });
      baseLinks.push({ path: AppRoutes.SUBSCRIPTIONS, title: 'Abos', icon: IconType.REFRESH });
      baseLinks.push({ path: AppRoutes.VOUCHERS, title: 'Gutscheine', icon: IconType.GIFT });
    }

    if (this.isAdmin) {
      baseLinks.push({ path: AppRoutes.ADMIN, title: 'Admin', isAdminMenu: true, icon: IconType.SETTINGS });
    }

    this.navigationLinks = baseLinks;
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    if (!this.isMenuOpen) {
      this.isAdminExpanded = false;
      this.isOrgDropdownOpen = false;
    }
  }

  toggleAdminSubmenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isAdminExpanded = !this.isAdminExpanded;
  }

  toggleOrgDropdown(event: Event): void {
    event.stopPropagation();
    this.isOrgDropdownOpen = !this.isOrgDropdownOpen;
  }

  selectOrganization(orgId: string, event: Event): void {
    event.stopPropagation();
    this.organizationService.selectOrganization(orgId);
    this.isOrgDropdownOpen = false;
  }


  logout(): void {
    this.authService.logout();
    this.isMenuOpen = false;
  }

  getUserInitial(user: User | null | undefined): string {
    const source = user?.resolvedDisplayName ?? user?.username;
    return source?.charAt(0)?.toUpperCase() || '?';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
